import { Button, Card, CardBody, Stack } from '@chakra-ui/react';
import { NotificationCategory } from '@pelicargo/types';
import { useError } from '@pelicargo/ui';
import { FreightServiceAddOn } from '@prisma/client';
import { uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { useConditionalResolver } from '../../hooks/useConditionalResolver';
import { useSelectedQuoteVariant } from '../../hooks/useSelectedQuoteVariant';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { DefaultContactsCard } from '../DefaultContactsCard';
import { FlightJourneyCard } from '../FlightJourneyCard';
import { InlandBookingForm } from '../Forms/InlandBookingForm';
import { QuoteVariantTabs } from '../QuoteVariantTabs';
import { RequestDetailsCard } from '../RequestDetailsCard';
import { SimpleForm } from '../SimpleForm';
import { TermsAndConditions } from '../TermsAndConditions';

const baseSchema = z.object({
  terms: z.boolean().refine((value) => value === true, { message: 'Please accept the terms and conditions' }),
  dropoff_at: z.coerce.date(),
  customer_ref: z.string().optional(),
  pickup_ref: z.string().optional(),
  freight_service_add_ons: z.nativeEnum(FreightServiceAddOn).array().optional(),
  dropoff_instructions: z.string().nullish(),
  awb_instructions: z.string().nullish(),
  additional_information: z.string().nullish(),
  trucking_name: z.string().nullish(),
  default_contacts: z.array(
    z.object({
      contact_id: z.coerce.number(),
      updates_for: z.array(z.nativeEnum(NotificationCategory, { required_error: 'Notification category is required' })),
    }),
  ),
});

type Props = {
  quote: TrpcOutputs['getQuote'];
  request: TrpcOutputs['getQuote']['request'];
};

export const FcaBooking = ({ quote, request }: Props) => {
  const { handleError } = useError();
  const navigate = useNavigate();
  const { targetAccount } = useTargetAccount();
  const { selectedQuoteVariantId } = useSelectedQuoteVariant();
  const quoteVariant = useMemo(() => {
    return quote?.quote_variants.find((variant) => variant.id === selectedQuoteVariantId);
  }, [quote?.quote_variants, selectedQuoteVariantId]);

  const { data: requestDefaultContacts } = trpc.getDefaultContactsByRequest.useQuery(
    {
      id: quote?.request_id,
      updates_for: NotificationCategory.Booking,
    },
    { enabled: !!quote?.request_id },
  );
  const { data: accountDefaultContacts } = trpc.getDefaultContactsByAccount.useQuery(
    {
      id: targetAccount?.id,
      updates_for: NotificationCategory.Booking,
    },
    { enabled: !!targetAccount?.id },
  );
  // Unique contacts by contact_id prioritizing request contacts
  const default_contacts = uniqBy([...(requestDefaultContacts || []), ...(accountDefaultContacts || [])], 'contact_id');
  const { fields, schema, resolver } = useConditionalResolver(baseSchema, quote?.id);

  type FormValues = z.infer<typeof schema>;

  const methods = useForm<FormValues>({
    mode: 'onBlur',
    resolver,
    defaultValues: {
      dropoff_at: quoteVariant?.inland_service_quote_variant?.quote?.dropoff_at,
      trucking_name: quoteVariant?.inland_service_quote_variant?.airline_ref,
      freight_service_add_ons: quote?.request?.freight_service_add_ons || [],
      customer_ref: request?.customer_ref || '',
      pickup_ref: quoteVariant?.inland_service_quote_variant?.quote?.pickup_ref || '',
      awb_instructions: quoteVariant?.inland_service_quote_variant?.quote?.awb_instructions || '',
      additional_information: quote?.additional_information ? quote?.additional_information : '',

      hs_code: request?.hs_code,
      commodity: request.commodity,
      is_military_related: request.is_military_related ?? false,
      is_consolidated: request?.is_consolidated ?? false,
    },
  });

  useEffect(() => {
    const dropoffAt = quoteVariant?.inland_service_quote_variant?.quote?.dropoff_at;
    const freightServiceAddOns = quote?.request?.freight_service_add_ons;
    const awbInstructions = quoteVariant?.inland_service_quote_variant?.quote?.awb_instructions;
    const additionalInformation = quote?.additional_information;
    const pickupRef = quoteVariant?.inland_service_quote_variant?.quote?.pickup_ref;

    if (dropoffAt) methods.setValue('dropoff_at', dropoffAt);
    if (freightServiceAddOns) methods.setValue('freight_service_add_ons', freightServiceAddOns);
    if (awbInstructions) methods.setValue('awb_instructions', awbInstructions);
    if (additionalInformation) methods.setValue('additional_information', additionalInformation);
    if (pickupRef) methods.setValue('pickup_ref', pickupRef);
  }, [
    methods,
    quoteVariant?.inland_service_quote_variant?.quote?.dropoff_at,
    quoteVariant?.inland_service_quote_variant?.quote?.awb_instructions,
    quote?.request?.freight_service_add_ons,
    quote?.additional_information,
    quoteVariant?.inland_service_quote_variant?.quote?.pickup_ref,
  ]);

  useEffect(() => {
    if (default_contacts?.length) {
      const contacts = default_contacts.map((contact) => ({
        contact_id: String(contact.contact_id),
        updates_for: contact.updates_for,
      }));
      methods.setValue(
        'default_contacts',
        contacts.map((contact) => ({
          contact_id: parseInt(contact.contact_id),
          updates_for: contact.updates_for as NotificationCategory[],
        })),
      );
    }
  }, [default_contacts, default_contacts?.length, methods]);

  const bookQuoteVariant = trpc.bookQuoteVariant.useMutation();

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        if (!quoteVariant) {
          handleError('Please select a quote variant.');
          return;
        }

        const requestId = quote?.request?.id;
        const quoteId = quote?.id;

        await bookQuoteVariant.mutateAsync({
          id: selectedQuoteVariantId,

          customerRef: values.customer_ref,
          pickupRef: values.pickup_ref,

          additionalInformation: values.additional_information,
          dropoffInstructions: values.dropoff_instructions,
          awbInstructions: values.awb_instructions,
          dropoffBy: values.dropoff_at,
          freightServiceAddOns: values.freight_service_add_ons,
          truckingName: values.trucking_name,
          defaultContacts:
            values?.default_contacts?.map((contact) => ({
              contact_id: Number(contact.contact_id),
              updates_for: contact?.updates_for || [],
            })) || [],

          commodity: values.commodity,
          hs_code: values.hs_code,
          is_military_related: values.is_military_related ?? false,
          is_consolidated: values.is_consolidated,
          emergency_contact: values.emergency_contact,
        });

        navigate(`/requests/${requestId}/quotes/${quoteId}/booked`);
      } catch (error) {
        handleError(error);
      }
    },
    [bookQuoteVariant, handleError, navigate, quote?.id, quote?.request?.id, quoteVariant, selectedQuoteVariantId],
  );

  return (
    <SimpleForm onSubmit={handleSubmit} defaultValues={{ default_contacts }} {...(methods as any)}>
      <Stack direction={{ base: 'column', lg: 'row' }} w="full" spacing="6">
        <Stack h="fit-content" w="full">
          <Card h="auto" px="4" py="3">
            <CardBody p="0">
              <Stack spacing="6">
                <InlandBookingForm quote={quote} conditionalFields={fields} />
                <DefaultContactsCard notificationCategory={NotificationCategory.Booking} />
              </Stack>
            </CardBody>
          </Card>
        </Stack>
        <Stack w="full" spacing="6">
          <Card h="auto">
            <CardBody p="0">
              <QuoteVariantTabs
                quote={quote}
                quoteVariants={quote?.quote_variants || []}
                showBookingLink={false}
                navigateOnSelection
              />
              <Stack p="4">
                <TermsAndConditions airline={quote?.airline} />
                <Button colorScheme="primary" size="lg" type="submit" isLoading={bookQuoteVariant?.isLoading}>
                  Request Booking
                </Button>
              </Stack>
            </CardBody>
          </Card>
          <FlightJourneyCard showHeader quote={quote} quoteVariant={quoteVariant} />
          <RequestDetailsCard request={request} quote={quote} />
        </Stack>
      </Stack>
    </SimpleForm>
  );
};
